import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Plus, Upload, Eye, Trash2, Edit } from "lucide-react";
import { AddOfferDialog } from "./AddOfferDialog";
import { EditOfferDialog } from "./EditOfferDialog";
import { ViewOfferModal } from "./ViewOfferModal";
import { getFormattedDateTime } from "@/utils";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { BulkUploadModal } from "./BulkUploadModal";
import { CSV_UPLOAD_TYPES } from "@/constants/common.constant";
import { MerchantApi } from "@/api";
import { PermissionGuard } from "@/components/common/PermissionGuard";
import { PERMISSION_CODES } from "@/constants/permissions";

interface Offer {
  id: string;
  name: string;
  description: string;
  discountType: string;
  discountValue: string;
  status: string;
  category: string;
  startDate: string;
  endDate: string;
  bannerImage?: string;
  offerUrl: string;
  terms: string;
  // Offline merchant fields
  eligiblePurchase?: string;
  colorCode?: string;
  // Full API response for detailed view
  fullOfferData?: any;
}

interface OffersTabProps {
  initialOffers: Offer[];
  merchantId: string;
  merchantCode?: string;
  merchant?: any; // Add merchant data to access businessType
}

export const OffersTab: React.FC<OffersTabProps> = ({
  initialOffers,
  merchantId,
  merchantCode,
  merchant,
}) => {
  const [offers, setOffers] = useState<Offer[]>(initialOffers);
  const [addOfferOpen, setAddOfferOpen] = useState(false);
  const [editOfferOpen, setEditOfferOpen] = useState(false);
  const [offerToEdit, setOfferToEdit] = useState<Offer | null>(null);
  const [viewOfferOpen, setViewOfferOpen] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState<Offer | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [offerToDelete, setOfferToDelete] = useState<string | null>(null);
  const [isDeletingOffer, setIsDeletingOffer] = useState(false);
  const [isBulkOfferUploadOpen, setIsBulkOfferUploadOpen] = useState(false);
  const [categories, setCategories] = useState<{ id: string; name: string }[]>(
    []
  );
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [uploadFailures, setUploadFailures] = useState<any[]>([]);

  const handleAddOffer = (newOffer: Offer) => {
    setOffers([...offers, newOffer]);
  };

  const handleEditOffer = (offer: Offer) => {
    console.log("OffersTab - handleEditOffer called with:", offer);
    setOfferToEdit(offer);
    setEditOfferOpen(true);
  };

  const handleUpdateOffer = (updatedOffer: Offer) => {
    setOffers(
      offers.map((offer) =>
        offer.id === updatedOffer.id ? updatedOffer : offer
      )
    );
  };

  const handleDeleteOffer = (offerId: string) => {
    setOfferToDelete(offerId);
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteOffer = async () => {
    if (!offerToDelete) return;

    setIsDeletingOffer(true);
    try {
      await MerchantApi.deleteOffer(offerToDelete);
      setOffers(offers.filter((offer) => offer.id !== offerToDelete));
      setDeleteConfirmOpen(false);
      setOfferToDelete(null);
      toast.success("Discount deleted successfully");
    } catch (error: any) {
      toast.error(error?.message || "Failed to delete discount");
    } finally {
      setIsDeletingOffer(false);
    }
  };

  const handleViewOffer = (offer: Offer) => {
    setSelectedOffer(offer);
    setViewOfferOpen(true);
  };

  const getStatusBadgeStyle = (status: string) => {
    return status === "Active"
      ? "bg-green-100 text-green-800 hover:bg-green-100"
      : "bg-red-100 text-red-800 hover:bg-red-100";
  };

  const formatDate = (dateString: string) => {
    return getFormattedDateTime(dateString) || "Invalid date";
  };

  // Fetch offers after bulk upload
  // `uploadResult` comes from the bulk upload API and can contain
  // success/failure counts and detailed failure reasons.
  const handleBulkOfferUpload = async (uploadResult: any) => {
    try {
      // Safely read summary info from the upload response
      const {
        successCount = 0,
        failureCount = 0,
        failures = [],
      } = uploadResult || {};

      // Show success summary (even if there are partial failures)
      if (successCount > 0 && failureCount === 0) {
        toast.success(`Successfully uploaded ${successCount} discount(s).`);
      } else if (successCount > 0 && failureCount > 0) {
        toast.success(
          `Uploaded ${successCount} discount(s) with ${failureCount} error(s).`
        );
      }

      // Show detailed error reasons if there are failures
      if (failureCount > 0 && Array.isArray(failures) && failures.length > 0) {
        setUploadFailures(failures);
        setIsErrorModalOpen(true);
        toast.error(
          `Failed to upload ${failureCount} record(s). Please review the error details.`
        );
      }

      // After handling messages, always refetch offers to reflect successful uploads
      const response = await import("@/api").then(({ MerchantApi }) =>
        MerchantApi.getMerchantOffers({
          merchantId,
          sortOrder: "desc",
        })
      );
      const mapApiOfferToUiOffer = (apiOffer: any) => ({
        id: apiOffer.id,
        name: apiOffer.title,
        description: apiOffer.description,
        discountType: "Cashback",
        discountValue: apiOffer.cashbackPercentage
          ? `${apiOffer.cashbackPercentage}%`
          : "",
        status: "Active",
        category: apiOffer.categoryVO?.categoryName || "",
        startDate: apiOffer.startDateInMillis
          ? new Date(apiOffer.startDateInMillis).toISOString()
          : "",
        endDate: apiOffer.endDateInMillis
          ? new Date(apiOffer.endDateInMillis).toISOString()
          : "",
        bannerImage: apiOffer.imageUri,
        offerUrl: apiOffer.offerURL,
        terms: apiOffer.termsAndConditions,
        // Include offline merchant fields
        eligiblePurchase: apiOffer.eligiblePurchase || "",
        colorCode: apiOffer.colorCode || "",
      });
      setOffers((response?.data || []).map(mapApiOfferToUiOffer));
      setIsBulkOfferUploadOpen(false);
    } catch (error) {
      toast.error(
        error?.message || "Failed to fetch updated discounts after upload"
      );
    }
  };

  // Fetch categories when Add Offer dialog is opened
  const handleOpenAddOffer = async () => {
    setAddOfferOpen(true);
    await fetchCategories();
  };

  // Fetch categories when Edit Offer dialog is opened
  const handleOpenEditOffer = async (offer: Offer) => {
    setOfferToEdit(offer);
    setEditOfferOpen(true);
    await fetchCategories();
  };

  // Common function to fetch categories
  const fetchCategories = async () => {
    setCategoriesLoading(true);
    try {
      const response = await MerchantApi.getCategoriesUsingMerchantId(
        merchantId
      );
      setCategories(
        (response?.data || []).map((cat: any) => ({
          id: String(cat.id),
          name: cat.categoryName,
        }))
      );
    } catch (error) {
      toast.error(error?.message || "Failed to fetch categories");
      setCategories([]);
    } finally {
      setCategoriesLoading(false);
    }
  };

  return (
    <Card className="border-0 shadow-sm bg-white">
      <CardHeader className="flex flex-row items-center justify-between border-b border-gray-100 bg-gray-50 p-6">
        <CardTitle className="text-lg font-medium text-gray-800">
          Discount
        </CardTitle>
        <div className="flex gap-2">
          <PermissionGuard
            permissionCode={PERMISSION_CODES.CREATE_MERCHANT_OFFER}
          >
            <Button
              size="sm"
              variant="outline"
              className="flex items-center gap-2"
              onClick={() => setIsBulkOfferUploadOpen(true)}
            >
              <Upload className="h-4 w-4" /> Bulk Upload
            </Button>
          </PermissionGuard>
          <PermissionGuard
            permissionCode={PERMISSION_CODES.CREATE_MERCHANT_OFFER}
          >
            <Button
              size="sm"
              className="flex items-center gap-2"
              onClick={handleOpenAddOffer}
            >
              <Plus className="h-4 w-4" /> Add Discount
            </Button>
          </PermissionGuard>
        </div>
      </CardHeader>
      <CardContent className="p-6">
        {offers.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <div className="mx-auto w-32 h-32 mb-4">
              <img
                src="/lovable-uploads/4c75fc6c-0171-49e7-9e79-ed3e2d9d4c44.png"
                alt="No discounts"
                className="w-full h-full object-contain"
              />
            </div>
            <p className="text-lg font-medium mb-1">No Discounts Found</p>
            <p className="mb-4">
              No discounts are currently added to this merchant.
            </p>
            <PermissionGuard permissionCode={PERMISSION_CODES.UPDATE_MERCHANT}>
              <Button
                size="sm"
                variant="outline"
                className="gap-2"
                onClick={() => setAddOfferOpen(true)}
              >
                <Plus className="h-4 w-4" /> Create Your First Discount
              </Button>
            </PermissionGuard>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {offers.map((offer) => (
              <Card
                key={offer.id}
                className="border border-gray-200 hover:shadow-md transition-shadow"
              >
                <CardContent className="p-0">
                  <div className="w-full h-32 overflow-hidden rounded-t-lg bg-gray-100 flex items-center justify-center">
                    <img
                      src={offer.bannerImage || "/placeholder.svg"}
                      alt={offer.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-4">
                    <CardHeader className="p-0 mb-3">
                      <div className="flex justify-between items-start mb-2">
                        <CardTitle className="text-base font-semibold text-gray-900 line-clamp-1">
                          {offer.name}
                        </CardTitle>
                        <Badge
                          variant="outline"
                          className={`text-xs ${getStatusBadgeStyle(
                            offer.status
                          )}`}
                        >
                          {offer.status}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-600 line-clamp-2">
                        {offer.description}
                      </p>
                    </CardHeader>
                    <div className="space-y-2 mb-4">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Category:</span>
                        <span className="font-medium">{offer.category}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Valid until:</span>
                        <span className="font-medium">
                          {formatDate(offer.endDate)}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleViewOffer(offer)}
                        className="flex-1 text-xs"
                      >
                        <Eye className="h-3 w-3 mr-1" /> View
                      </Button>
                      <PermissionGuard
                        permissionCode={PERMISSION_CODES.UPDATE_MERCHANT_OFFER}
                      >
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleOpenEditOffer(offer)}
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        >
                          <Edit className="h-3 w-3" />
                        </Button>
                      </PermissionGuard>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDeleteOffer(offer.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </CardContent>
      <AddOfferDialog
        open={addOfferOpen}
        onOpenChange={setAddOfferOpen}
        onAddOffer={handleAddOffer}
        categories={categories}
        categoriesLoading={categoriesLoading}
        merchantId={merchantId}
        merchant={merchant}
      />
      <EditOfferDialog
        open={editOfferOpen}
        onOpenChange={setEditOfferOpen}
        onUpdateOffer={handleUpdateOffer}
        categories={categories}
        categoriesLoading={categoriesLoading}
        merchantId={merchantId}
        merchant={merchant}
        offerToEdit={offerToEdit}
      />
      {selectedOffer && (
        <ViewOfferModal
          offer={selectedOffer}
          open={viewOfferOpen}
          onOpenChange={setViewOfferOpen}
        />
      )}
      {/* Delete Discount Confirmation Dialog */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Discount</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this discount? This action cannot
              be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteOffer}
              disabled={isDeletingOffer}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeletingOffer ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <BulkUploadModal
        uploadType={CSV_UPLOAD_TYPES.OFFER}
        merchantCode={merchantCode}
        open={isBulkOfferUploadOpen}
        onOpenChange={setIsBulkOfferUploadOpen}
        title="Bulk Upload Discounts"
        type="offer"
        onUploadComplete={handleBulkOfferUpload}
      />

      {/* Bulk Upload Error Details Modal */}
      <Dialog open={isErrorModalOpen} onOpenChange={setIsErrorModalOpen}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Bulk Upload Errors</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {uploadFailures && uploadFailures.length > 0 ? (
              uploadFailures.map((failure, index) => (
                <div
                  key={index}
                  className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
                >
                  <div className="font-medium">#{index + 1}</div>
                  <div>{failure?.reason || "Unknown error"}</div>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-600">
                No detailed error information available.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsErrorModalOpen(false)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
